import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  getStates,
  getCompanyEmployerSubContractors
} from '../redux/features/performerClient.slice';
import { useGetUserCompanies } from '../backend/query';
import { FULL_USER_COMPANIES_PAYLOAD } from '../backend/license';
import {
  getStateRegisterQue,
  addStateRegisterQue
} from '../redux/features/stateRegisterQue.slice';
import { DEFAULT_OPTIONS_PAYLOAD } from '../components/common/Table';
import {
  PerformerClientFilterState,
  PerformerClientSearchConfig,
  INITIAL_PERFORMER_CLIENT_FILTER_STATE,
  INITIAL_PERFORMER_CLIENT_SEARCH_CONFIG,
  buildPerformerClientQueryPayload,
  getPerformerClientResetFields,
  getPerformerClientFilterKey,
  PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS_CLRA,
  PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS_CLRA
} from '../utils/performerClientRegisters.utils';

export const usePerformerClientRegisterFilters = () => {
  const [filters, setFilters] = useState<PerformerClientFilterState>(INITIAL_PERFORMER_CLIENT_FILTER_STATE);

  const updateFilter = useCallback((field: keyof PerformerClientFilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...getPerformerClientResetFields(field)
    }));
  }, []);

  const updateMultipleFilters = useCallback((updates: Partial<PerformerClientFilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_PERFORMER_CLIENT_FILTER_STATE);
  }, []);

  return { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters };
};

// Alias for backwards compatibility
export const useFilters = usePerformerClientRegisterFilters;

export const usePerformerClientRegisterSearchConfig = () => {
  const [searchConfig, setSearchConfig] = useState<PerformerClientSearchConfig>(INITIAL_PERFORMER_CLIENT_SEARCH_CONFIG);

  const updateSearchConfig = useCallback((updates: Partial<PerformerClientSearchConfig>) => {
    setSearchConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSearchConfig = useCallback(() => {
    setSearchConfig(INITIAL_PERFORMER_CLIENT_SEARCH_CONFIG);
  }, []);

  return { searchConfig, updateSearchConfig, resetSearchConfig };
};

// Alias for backwards compatibility
export const useSearchConfig = usePerformerClientRegisterSearchConfig;

export const usePerformerClientRegisterData = (filters: PerformerClientFilterState, searchConfig: PerformerClientSearchConfig) => {
  const dispatch = useAppDispatch();

  const fetchData = useCallback((
    filtersOverride?: PerformerClientFilterState,
    searchConfigOverride?: Partial<PerformerClientSearchConfig>,
    overrides: Partial<any> = {},
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ) => {
    const effectiveFilters = filtersOverride || filters;
    const effectiveSearchConfig = searchConfigOverride ? { ...searchConfig, ...searchConfigOverride } : searchConfig;
    const payload = buildPerformerClientQueryPayload(effectiveFilters, effectiveSearchConfig, overrides, sortField, sortDirection);
    dispatch(getStateRegisterQue(payload));
  }, [filters, searchConfig, dispatch]);

  const fetchWithSearch = useCallback((searchInput: string) => {
    const basePayload = buildPerformerClientQueryPayload(
      filters,
      searchConfig,
      { que_id_eq: searchInput },
      undefined,
      undefined
    );
    // Override to always request CLRA includes when searching (don't know type beforehand)
    const payload = {
      ...basePayload,
      include: PERFORMER_CLIENT_INCLUDE_ASSOCIATION_COLUMNS_CLRA,
      includes: PERFORMER_CLIENT_INCLUDES_ASSOCIATIONS_CLRA
    };
    dispatch(getStateRegisterQue(payload));
  }, [filters, searchConfig, dispatch]);

  return { fetchData, fetchWithSearch };
};

// Alias for backwards compatibility
export const useStateRegisterData = usePerformerClientRegisterData;

export const usePerformerClientRegisterCompanyData = () => {
  const dispatch = useAppDispatch();
  const { userCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);
  const statesDetails = useAppSelector((state) => state.performerClient.statesDetails);

  const companies = userCompanies || [];
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedAssocCompanyId, setSelectedAssocCompanyId] = useState<string>('');

  const selectedCompany = companies.find((c: any) => c.id === selectedCompanyId);
  const associateCompanies = (selectedCompany && selectedCompany.associate_companies) || [];

  const selectedAssocCompany = associateCompanies.find((ac: any) => ac.id === selectedAssocCompanyId);
  const locations = (selectedAssocCompany && selectedAssocCompany.locations) || [];

  const totalStates = ((statesDetails || {}).data || {}).items || [];

  const fetchCompanies = useCallback(() => {
    // No-op: data comes from useGetUserCompanies
  }, []);

  const fetchAssociateCompanies = useCallback((companyId: string) => {
    setSelectedCompanyId(companyId);
  }, []);

  const fetchLocations = useCallback((associateCompanyId: string) => {
    setSelectedAssocCompanyId(associateCompanyId);
  }, []);

  const fetchStates = useCallback(() => {
    const payload = { ...DEFAULT_OPTIONS_PAYLOAD };
    dispatch(getStates(payload));
  }, [dispatch]);

  return {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates
  };
};

// Alias for backwards compatibility
export const useCompanyData = usePerformerClientRegisterCompanyData;

export const usePerformerClientRegisterEmployerData = () => {
  const dispatch = useAppDispatch();
  const [employers, setEmployers] = useState<any[]>([]);
  const [employerLocations, setEmployerLocations] = useState<any[]>([]);
  const [employerSubContractors, setEmployerSubContractors] = useState<any[]>([]);
  const [employerSubContractorsLocations, setEmployerSubContractorsLocations] = useState<any[]>([]);

  const buildEmployerPayload = useCallback((
    filters: PerformerClientFilterState,
    locations: any[],
    payloadType: 'employer' | 'employerLocation' | 'subContractor' | 'subContractorLocation'
  ) => {
    const loc = locations.find((l: any) => l.location_id === filters.locations[0]);
    const basePayload: any = {
      filters: {
        establishment_type_eq: filters.establishmentType || '',
        company_id_eq: filters.company || '',
        associate_company_id_eq: filters.associateCompany || '',
        company_location_mapping_id_eq: (loc || {}).company_location_mapping_id
      },
      per: 5000,
      page: 1,
      includeCentral: true
    };

    switch (payloadType) {
      case 'employer':
        basePayload.include = {
          employer: {
            only: ['id', 'name']
          }
        };
        break;
      case 'employerLocation':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.include = {
          employer_location: {
            only: ['id', 'location_name']
          }
        };
        break;
      case 'subContractor':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.filters['employer_location_id_eq'] = filters.employerLocation || null;
        basePayload.include = {
          sub_contractor: {
            only: ['id', 'name']
          }
        };
        break;
      case 'subContractorLocation':
        basePayload.filters['employer_id_eq'] = filters.employer || null;
        basePayload.filters['employer_location_id_eq'] = filters.employerLocation || null;
        basePayload.filters['sub_contractor_id_eq'] = filters.subContractor || null;
        basePayload.include = {
          sub_contractor_location: {
            only: ['id', 'location_name']
          }
        };
        break;
    }

    return basePayload;
  }, []);

  const fetchEmployers = useCallback((filters: PerformerClientFilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'employer');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempEmployers: any[] = [];
        items.forEach((x: any) => {
          if (x.employer_id && x.employer) {
            if (!tempEmployers.some((y: any) => y.id === x.employer.id)) {
              tempEmployers.push(x.employer);
            }
          }
        });
        setEmployers(tempEmployers);
      } else {
        setEmployers([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchEmployerLocations = useCallback((filters: PerformerClientFilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'employerLocation');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempLocations: any[] = [];
        items.forEach((x: any) => {
          if (x.employer_location_id && x.employer_location) {
            if (!tempLocations.some((y: any) => y.id === x.employer_location.id)) {
              tempLocations.push(x.employer_location);
            }
          }
        });
        setEmployerLocations(tempLocations);
      } else {
        setEmployerLocations([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchSubContractors = useCallback((filters: PerformerClientFilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'subContractor');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempSubContractors: any[] = [];
        items.forEach((x: any) => {
          if (x.sub_contractor_id && x.sub_contractor) {
            if (!tempSubContractors.some((y: any) => y.id === x.sub_contractor.id)) {
              tempSubContractors.push(x.sub_contractor);
            }
          }
        });
        setEmployerSubContractors(tempSubContractors);
      } else {
        setEmployerSubContractors([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  const fetchSubContractorLocations = useCallback((filters: PerformerClientFilterState, locations: any[]) => {
    const payload = buildEmployerPayload(filters, locations, 'subContractorLocation');
    dispatch(getCompanyEmployerSubContractors(payload)).then((response: any) => {
      if (response && response.payload && response.payload.data && response.payload.data.items) {
        const items = response.payload.data.items || [];
        const tempLocations: any[] = [];
        items.forEach((x: any) => {
          if (x.sub_contractor_location_id && x.sub_contractor_location) {
            if (!tempLocations.some((y: any) => y.id === x.sub_contractor_location.id)) {
              tempLocations.push(x.sub_contractor_location);
            }
          }
        });
        setEmployerSubContractorsLocations(tempLocations);
      } else {
        setEmployerSubContractorsLocations([]);
      }
    });
  }, [dispatch, buildEmployerPayload]);

  return {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations
  };
};

// Alias for backwards compatibility
export const useEmployerData = usePerformerClientRegisterEmployerData;
